import { SvgCalendar } from '@chilipiper/icons'
import { SvgError } from '@chilipiper/icons/src/design-system'
import { typeValidator } from '@chilipiper/utils'
import { CalendarDate, parseDate } from '@internationalized/date'
import React, { MutableRefObject, useEffect, useRef, useState } from 'react'
import { AriaDatePickerProps, mergeProps, useDatePicker } from 'react-aria'
import { DatePickerStateOptions, useDatePickerState } from 'react-stately'
import styled from 'styled-components'
import invariant from 'tiny-invariant'
import { Box, Flex, ScreenReaderOnly, Text } from '../../../core-components'
import { Events } from '../../../types'
import { IconButton } from '../../action'
import { IconTooltip } from '../../feedback/icon-tooltip/IconTooltip'
import { Icon } from '../../../../old/icon'
import { PositioningProps } from '../../types'
import { SettingLabel, SettingLabelContainer } from '../../wayfinding/setting-label/SettingLabel'
import { DateField, DateFieldWrapper } from '../base-calendar/DateField'
import { FieldPopover } from '../internal/FieldPopover'
import { DatePickerCalendarInternal } from './date-picker-calendar/DatePickerCalendar'

type Props = {
  'data-test-id'?: string
  errorMessage?: string
  eventsRef?: MutableRefObject<Events<'open'> | null | undefined>
  isDisabled?: boolean
  label: React.ReactNode
  onChange: (value: string | undefined) => void
  /** ISO 8601 date string without time */
  value?: string
  variant?: 'regular' | 'nested'
  size?: 'regular' | 'large'
} & PositioningProps

export const DatePicker = ({
  'data-test-id': dataTestId,
  value,
  label,
  onChange,
  errorMessage,
  variant = 'regular',
  isDisabled,
  eventsRef,
  size = 'regular',
  ...positioningProps
}: Props) => {
  invariant(
    typeValidator(DatePickerLabel)(label),
    'DatePicker: label must be a DatePicker.Label component'
  )
  const props: DatePickerStateOptions<CalendarDate> & AriaDatePickerProps<CalendarDate> = {
    value: value ? parseDate(value) : null,
    onChange: (date: CalendarDate | null) => {
      if (date) {
        onChange(date.toString())
      } else {
        onChange(undefined)
      }
    },
    label,
    errorMessage,
    isDisabled,
  }
  const state = useDatePickerState(props)
  const ref = React.useRef<HTMLDivElement>(null)
  const [openWithTrigger, setOpenWithTrigger] = useState(false)
  const [segmentToFocus, setSegmentToFocus] = useState<HTMLElement | undefined>(undefined)
  const {
    labelProps,
    descriptionProps,
    fieldProps,
    groupProps,
    buttonProps,
    calendarProps,
    dialogProps,
    errorMessageProps,
  } = useDatePicker(
    props,
    {
      ...state,
      setDateValue: (newVal: CalendarDate) => {
        setOpenWithTrigger(false)
        state.setDateValue(newVal)
      },
    },
    ref
  )

  const stateRef = useRef(state)
  stateRef.current = state

  useEffect(() => {
    if (eventsRef) {
      eventsRef.current = {
        open: () => {
          setTimeout(() => {
            setOpenWithTrigger(true)
            stateRef.current.open()
          }, 0)
        },
      }
      return () => (eventsRef.current = undefined)
    }
  }, [eventsRef])

  const handleSelectKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape') {
      state.close()
    }
  }

  const handleDateFieldWrapperClick = () => {
    if (!isDisabled) {
      state.open()
      segmentToFocus?.focus()
    }
  }

  return (
    <SettingLabelContainer
      isRequired={false}
      label={label}
      labelElement='div'
      labelProps={labelProps}
      descriptionProps={descriptionProps}
      {...positioningProps}
    >
      <FieldPopover
        dialogAriaProps={dialogProps}
        isOpen={state.isOpen}
        onOpenChange={newVal => {
          if (!newVal) {
            setOpenWithTrigger(false)
          }
          state.setOpen(newVal)
        }}
        trigger={({ positionRef, popoverRef, ...triggerProps }) => {
          return (
            <div ref={positionRef}>
              <StyledDatePickerTrigger
                boxShadow={
                  variant === 'regular' && !isDisabled && !errorMessage
                    ? 'border/section'
                    : undefined
                }
                borderRadius={variant === 'regular' ? 10 : undefined}
              >
                <DateFieldWrapper
                  {...groupProps}
                  data-test-id={dataTestId}
                  data-open={state.isOpen}
                  ref={ref}
                  onClick={handleDateFieldWrapperClick}
                  hasError={!!errorMessage}
                  borderRadius={variant === 'regular' ? 10 : undefined}
                  variant={variant}
                >
                  <DateField
                    {...fieldProps}
                    popoverRef={popoverRef}
                    datePickerState={state}
                    onSegmentToFocusChange={setSegmentToFocus}
                    size={size}
                  />
                  <Flex alignItems='center'>
                    {errorMessage && (
                      <>
                        {variant === 'regular' ? (
                          <Icon icon={SvgError} color='fg/caution' w={4} h={4} ml={3} />
                        ) : (
                          <IconTooltip variant='error' content={errorMessage} />
                        )}
                      </>
                    )}
                    <IconButton
                      isDisabled={isDisabled}
                      ml={3}
                      icon={SvgCalendar}
                      label='Open calendar'
                      size={6}
                      variant='ghost'
                      ariaProps={mergeProps(
                        {
                          onPress: () => setOpenWithTrigger(!state.isOpen),
                          onFocus: state.close,
                        },
                        buttonProps,
                        triggerProps
                      )}
                    />
                  </Flex>
                </DateFieldWrapper>
              </StyledDatePickerTrigger>
            </div>
          )
        }}
      >
        <DatePickerCalendarInternal
          {...calendarProps}
          autoFocus={openWithTrigger}
          onSelectKeyDown={handleSelectKeyDown}
        />
      </FieldPopover>
      {errorMessage && variant === 'regular' ? (
        <Text
          display='block'
          mt={2}
          color='fg/caution'
          textStyle='body-multiline-tight'
          {...errorMessageProps}
        >
          {errorMessage}
        </Text>
      ) : (
        <ScreenReaderOnly {...errorMessageProps}>{errorMessage}</ScreenReaderOnly>
      )}
    </SettingLabelContainer>
  )
}

const StyledDatePickerTrigger = styled(Box)`
  :hover {
    &::after {
      box-shadow: none;
    }
  }
`

const DatePickerLabel = (props: React.ComponentProps<typeof SettingLabel>) => (
  <SettingLabel {...props} />
)

DatePicker.Label = DatePickerLabel
